/**
 * Endpoint presets (v3 homepage) — one-click data wiring for the chart widgets,
 * shared by both editors (beta WidgetSettingsPanel + legacy vLandingConfig).
 *
 * Each preset bundles EVERYTHING an admin would otherwise type by hand for a
 * known `Report/*` endpoint: endpoint + method + response field mappings +
 * filter scaffolding + sensible labels. Applying a preset overwrites those
 * keys on the widget definition and leaves everything else (title, mode,
 * limits…) untouched.
 *
 * Keep this catalogue in lock-step with rm-api's report.controller.ts — these
 * are the shipped homepage chart endpoints and their REAL row shapes:
 *   · Report/evaluationProgress → [{ status, status_count }]
 *   · Report/jobProfileProgress → [{ status, colour, position, count }],
 *     accepts filters[] on docType (tmpl_name) + status (lifecycle name)
 *   · Report/headcountByLevel   → { rows:[{ level, position, headcount }],
 *     levels, jobFamilyGroups, jobFamilies }, accepts body arrays
 *     level / jobFamilyGroup / jobFamily
 */

export const DONUT_PRESETS = [
  {
    id: 'evaluationProgress',
    label: 'Evaluation progress — Report/evaluationProgress',
    values: {
      dataSource: 'api',
      apiEndpoint: 'Report/evaluationProgress',
      apiMethod: 'POST',
      segmentMapping: { labelField: 'status', valueField: 'status_count', colourField: 'colour' },
      apiFilters: [],
      apiParams: [],
      centreLabel: 'evaluations',
      doneLabels: 'Completed',
      linkText: 'View job evaluations',
    },
  },
  {
    id: 'jobProfileProgress',
    label: 'Document progress by status — Report/jobProfileProgress',
    values: {
      dataSource: 'api',
      apiEndpoint: 'Report/jobProfileProgress',
      apiMethod: 'POST',
      segmentMapping: { labelField: 'status', valueField: 'count', colourField: 'colour' },
      // Fill the docType values (the "available doc types") after applying —
      // >1 value turns on the doc-type chip switcher.
      apiFilters: [{ field: 'docType', condition: 'include', values: [] }],
      apiParams: [],
      centreLabel: '{{docType}}s',
      linkText: 'View {{docType}}s',
    },
  },
];

export const BAR_PRESETS = [
  {
    id: 'headcountByLevel',
    label: 'Headcount by level — Report/headcountByLevel',
    values: {
      dataSource: 'api',
      apiEndpoint: 'Report/headcountByLevel',
      apiMethod: 'POST',
      rowMapping: { labelField: 'level', valueField: 'headcount' },
      // Cascading filters — options ride back on the same response
      // (allItemsField), selected values post as body arrays keyed by `key`.
      filters: [
        { key: 'level', label: 'Level', multiple: true, allItemsField: 'levels' },
        { key: 'jobFamilyGroup', label: 'Job Family Group', multiple: true, allItemsField: 'jobFamilyGroups' },
        { key: 'jobFamily', label: 'Job Family', multiple: true, allItemsField: 'jobFamilies' },
      ],
      apiParams: [],
      categoryLabel: 'Level',
      unitLabel: 'employees',
      linkText: 'View employees by level',
    },
  },
];

/** Preset catalogue per widget type (beta registry names). */
export const ENDPOINT_PRESETS = {
  DonutChart: DONUT_PRESETS,
  BarChart: BAR_PRESETS,
};

/** Look a preset up by id within a catalogue. */
export function presetById(list, id) {
  return (list || []).find((p) => p && p.id === id) || null;
}

/**
 * Apply a preset onto a definition/props bag via the caller's setter
 * (`(key, value) => void` — beta uses setPath, legacy uses Vue.set) so
 * reactivity caveats stay in the presentation layer. Values are deep-cloned
 * so later edits never mutate the catalogue.
 */
export function applyEndpointPreset(preset, set) {
  if (!preset || typeof set !== 'function') return;
  Object.entries(preset.values || {}).forEach(([key, value]) => {
    set(key, JSON.parse(JSON.stringify(value)));
  });
}
